// Automation Rules Engine — shared utilities and shape factories.
//
// Rules live on each stage inside companies/{companyId}/meta/extras under
// jobTypes[].defaultStages[].automationRules[]. Events (instances of a rule
// firing on a specific job) live in the top-level automationEvents collection.

export function newRuleId() {
  return `rule_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function newAutomationEventId() {
  return `aev_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// Default-shape rule. Used by the Settings UI when adding a new rule. All
// fields are explicit so the saved object matches the spec exactly.
export function makeDefaultRule(overrides = {}) {
  return {
    id: newRuleId(),
    triggerType: 'onEnter',          // 'onEnter' | 'afterDays'
    daysThreshold: 3,                // only meaningful when triggerType === 'afterDays'
    taskType: 'standard',            // 'standard' | 'decision'
    description: '',
    assigneeType: 'default',         // 'default' | 'noDefault'
    defaultAssignees: [],            // array of userIds
    confirmationMode: 'requireConfirmation', // 'requireConfirmation' | 'autoRun' (afterDays always auto-runs)
    emailNotification: true,
    active: true,
    ...overrides,
  };
}

// Defensive: coerce any rule object loaded from Firestore into the canonical
// shape so missing fields don't break the editor or runtime.
export function normalizeRule(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    id: raw.id || newRuleId(),
    triggerType: raw.triggerType === 'afterDays' ? 'afterDays' : 'onEnter',
    daysThreshold: typeof raw.daysThreshold === 'number' && raw.daysThreshold > 0 ? raw.daysThreshold : 3,
    taskType: raw.taskType === 'decision' ? 'decision' : 'standard',
    description: typeof raw.description === 'string' ? raw.description : '',
    assigneeType: raw.assigneeType === 'noDefault' ? 'noDefault' : 'default',
    defaultAssignees: Array.isArray(raw.defaultAssignees) ? raw.defaultAssignees.filter(Boolean) : [],
    confirmationMode: raw.confirmationMode === 'autoRun' ? 'autoRun' : 'requireConfirmation',
    emailNotification: raw.emailNotification !== false,
    active: raw.active !== false,
  };
}

// Who a fired rule lands on, in words. Shared by both descriptions below.
function assigneeWords(r, companyUsers) {
  const names = (r.defaultAssignees || [])
    .map(uid => {
      const u = (companyUsers || []).find(x => x.uid === uid);
      if (!u) return null;
      return (u.firstName && u.lastName) ? `${u.firstName} ${u.lastName}`.trim() : (u.email || 'a team member');
    })
    .filter(Boolean);
  if (r.assigneeType === 'noDefault' || names.length === 0) return null;
  return names.join(', ');
}

// ONE SENTENCE, PLAIN LANGUAGE, for the Settings → Automations list: when it
// fires and what it does. Deliberately a SEPARATE function from describeRule
// below rather than a reshaping of it — the Simulator's copy is written to
// answer "what would happen if I moved this job", present tense and hedged
// ("will be assigned to"), and this one answers "what does this rule do",
// which reads better as an instruction. Both normalize through the same
// normalizeRule and share assigneeWords, so neither can describe a field the
// other doesn't know about.
export function describeAutomation(rule, stageName, companyUsers) {
  const r = normalizeRule(rule);
  if (!r) return '';
  const stage = stageName || 'this stage';
  const when = r.triggerType === 'afterDays'
    ? `After ${r.daysThreshold} day${r.daysThreshold === 1 ? '' : 's'} in ${stage}`
    : `When a job reaches ${stage}`;
  const kind = r.taskType === 'decision' ? 'push task' : 'task';
  const what = r.description ? `“${r.description}”` : 'an unnamed task';
  const who = assigneeWords(r, companyUsers) || 'whoever moves the job';
  const confirm = (r.triggerType === 'onEnter' && r.confirmationMode === 'requireConfirmation')
    ? ', after a confirmation prompt' : '';
  const email = r.emailNotification ? ', and email them' : '';
  return `${when}, create a ${kind} ${what} for ${who}${confirm}${email}.`;
}

// Render a plain-English description of a rule for the Automation Simulator.
export function describeRule(rule, stageName, companyUsers) {
  const r = normalizeRule(rule);
  if (!r) return '';
  const taskKind = r.taskType === 'decision' ? 'decision task' : 'standard task';
  const desc = r.description ? `'${r.description}'` : '(unnamed task)';
  // Shares assigneeWords with describeAutomation so the two descriptions can
  // never disagree about WHO a rule lands on — only about how they say it.
  const assigneeLabel = assigneeWords(r, companyUsers) || 'the user who triggered the change';
  const emailNote = r.emailNotification ? ' with email notification' : '';

  if (r.triggerType === 'afterDays') {
    return `After ${r.daysThreshold} day${r.daysThreshold === 1 ? '' : 's'} in ${stageName || 'this stage'}, a ${taskKind} ${desc} will be assigned to ${assigneeLabel}${emailNote}.`;
  }
  return `A ${taskKind} ${desc} will be assigned to ${assigneeLabel}${emailNote}.`;
}
